import { useApiContext } from '@/contexts/apiContext';
import { TableCell, TableRow } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EntityType } from '../entities/entityType';

type Props = {
  consumedEvent?: any;
};

const ConsumedEventView = ({ consumedEvent }: Props) => {
  const apiContext = useApiContext();
  const { t } = useTranslation();

  const [entityDefs, setEntityDefs] = useState<any[]>([]);
  const [entities, setEntities] = useState<any[]>([]);
  const [eventDefs, setEventDefs] = useState<any[]>([]);

  const [entityType, setEntityType] = useState<any>(consumedEvent?.entityType);
  const [entityDefId, setEntityDefId] = useState<string>(consumedEvent?.entityDefId);
  const [entityId, setEntityId] = useState<string>(consumedEvent?.entityId);
  const [eventDef, setEventDef] = useState<any>(eventDefs.find((ed) => ed.id === consumedEvent?.eventDefId));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>();

  useEffect(() => {
    const getEntityDefs = async () => {
      try {
        setLoading(true);
        const response =
          entityType === EntityType.agentInstance
            ? await apiContext.getAgentDefs()
            : entityType === EntityType.dataSourceInstance
              ? await apiContext.getDataSourceDefs()
              : entityType === EntityType.userAppInstance
                ? await apiContext.getUserAppDefs()
                : [];

        const entityDefs = [{ value: '*', label: 'Any type' }];
        entityDefs.push(...response.map((ent) => ({ value: ent.id, label: ent.name })));
        setEntityDefs(entityDefs);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    setEntityDefs([{ value: '*', label: 'Any type' }]);
    setEntities([{ value: '*', label: 'Any instance' }]);
    setEventDefs([]);
    setEntityDefId(consumedEvent?.entityDefId);
    setEntityId(consumedEvent?.entityId);
    setEventDef(eventDefs.find((ed) => ed.id === consumedEvent?.eventDefId));

    if (entityType) {
      getEntityDefs();
    }
  }, [entityType]);

  useEffect(() => {
    const getEntities = async () => {
      try {
        setLoading(true);
        const response =
          entityType === EntityType.agentInstance
            ? await apiContext.getAgentInstancesOfType(entityDefId)
            : entityType === EntityType.dataSourceInstance
              ? await apiContext.getDataSourceInstancesOfType(entityDefId)
              : entityType === EntityType.userAppInstance
                ? await apiContext.getUserAppInstancesOfType(entityDefId)
                : [];
        const entities = [{ value: '*', label: 'Any instance' }];
        entities.push(...response.map((ent) => ({ value: ent.id, label: ent.name })));
        setEntities(entities);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    const getEventDefs = async () => {
      try {
        setLoading(true);
        const response = await apiContext.getEventDefs(entityType, entityDefId);
        const eventDefs = response.map((ent) => ({ id: ent.id, label: ent.eventLabel, eventType: ent.eventType }));
        setEventDefs(eventDefs);
        setEventDef(eventDefs.find((ed) => ed.id === consumedEvent?.eventDefId));
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    setEntities([{ value: '*', label: 'Any instance' }]);
    setEventDefs([]);
    setEntityId(consumedEvent?.entityId);
    setEventDef(eventDefs.find((ed) => ed.id === consumedEvent?.eventDefId));
    if (entityType && entityDefId === '*') {
      setEntityId('*');
    } else if (entityType && entityDefId) {
      getEntities();
    }

    if (entityType && entityDefId) {
      getEventDefs();
    }
  }, [entityType, entityDefId]);

  useEffect(() => {
    setEventDefs([]);
  }, [entityType]);

  return (
    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
      <TableCell>{t(`common.entityType.${consumedEvent.entityType}`)}</TableCell>
      <TableCell>{entityDefs.find((ed) => ed.value === consumedEvent.entityDefId)?.label}</TableCell>
      <TableCell>{entities.find((ed) => ed.value === consumedEvent.entityId)?.label}</TableCell>
      <TableCell>{eventDefs.find((ed) => ed.id === consumedEvent.eventDefId)?.label}</TableCell>
    </TableRow>
  );
};

export default ConsumedEventView;
